import * as React from 'react';
import { Box, Text, Menu, HStack, Button, MenuButtonProps, MenuListProps, Tag, Flex } from '@televet/kibble-ui';
import partial from 'lodash-es/partial';

export enum TagDisabledState {
  Inactive = 'Inactive',
  Deleted = 'Deleted',
}

export interface AutomationTag {
  label: string;
  value: string;
}

interface AutomationsTagBlockProps {
  title?: string;
  buttonProps?: MenuButtonProps;
  listProps: MenuListProps;
  tags: AutomationTag[];
  canBeDeleted?: boolean;
  onClearAll?: () => void;
  onRemoveTag?: (tag: string, selectedTags: AutomationTag[]) => void;
  handleDelete?: () => void;
  hasError?: boolean;
  errorText?: string;
}

export const AutomationsTagBlock = ({
  title,
  buttonProps = {
    size: 'sm',
    variant: 'secondarySubtle',
    leftIconName: 'plus',
    text: 'Add',
    showRightIcon: false,
  },
  listProps,
  tags,
  canBeDeleted = false,
  onClearAll,
  onRemoveTag,
  handleDelete,
  hasError = false,
  errorText,
}: AutomationsTagBlockProps): JSX.Element => {
  const handleRemoveTag = (value: string): void => {
    if (onRemoveTag) {
      onRemoveTag(value, tags);
    }
  };

  return (
    <Box
      position="relative"
      mb={3}
      py={2}
      pl={4}
      pr={2}
      bg="background.subtle"
      borderRadius="lg"
      boxShadow={hasError ? `0 0 0 2px var(--chakra-colors-border-danger)` : ''}
      border="1px"
      borderColor="border.default"
      className="Automations__TagBlock"
    >
      <Flex justifyContent="space-between">
        <HStack spacing={1} minH={10}>
          {title && (
            <Text mr={2} fontWeight="bold" className="Automations__TagBlock-Title">
              {title}
            </Text>
          )}
          <Menu buttonProps={buttonProps} listProps={listProps} />
          <Button
            className="Automations__TagBlock-ClearAllButton"
            size="sm"
            iconName="arrowCircle"
            variant="ghostNeutral"
            onClick={onClearAll}
            isDisabled={tags.length <= 0}
          >
            Clear All
          </Button>
        </HStack>
        {canBeDeleted && (
          <Box>
            <Button
              className="Automations__TagBlock-DeleteButton"
              iconName="trashcan"
              variant="ghostNeutral"
              onClick={handleDelete}
              justifySelf="flex-end"
            />
          </Box>
        )}
      </Flex>
      {Boolean(tags.length) && (
        <Flex
          mt={2}
          pb={1}
          alignContent="space-between"
          flexWrap="wrap"
          justifyContent="flex-start"
          alignItems="flex-start"
          className="Automations__TagBlock-TagContainer"
        >
          {tags.map((tag) => {
            const isDisabled =
              tag.label.includes(TagDisabledState.Inactive) || tag.label.includes(TagDisabledState.Deleted);

            return (
              <Tag
                className="Automations__TagBlock-Tag"
                mr={1}
                mb={1}
                key={tag.value}
                label={tag.label}
                onClose={partial(handleRemoveTag, tag.value)}
                closeButtonDisplay="always"
                color={isDisabled ? 'text.placeholder' : 'text.default'}
                size="md"
                fontWeight="regular"
              />
            );
          })}
        </Flex>
      )}
      {hasError && (
        <Text mt={2} size="xs" as="p" variant="danger">
          {errorText ? errorText : 'This field contains an error'}
        </Text>
      )}
    </Box>
  );
};
